
    import Vue from "vue";
    import {Component, Prop, Watch} from "vue-property-decorator";
    import {VueInject} from "@/VueIntegration/VueInjectable";
    import {Synchroniser} from "@/Synchroniser";
    import {Slideshow, SlideshowEvents} from "@/slideshow";
    import {SlideData} from "@/Core/SlideshowData";
    import {TutorialAttemptApi} from "@/Api/TutorialAttemptApi";
    import ProgressIndicator from "./ProgressIndicator.vue";
    import Slide from "./Slide.vue";
    import Navigation from "./Navigation.vue";
    import TitleSlide from "./TitleSlide.vue";
    import ClosedCaptions from "./ClosedCaptions.vue";
    import {toggleFullscreen} from "@/Fullscreen";
    import MultipleChoice from "../Interactivity/MultipleChoice.vue";
    import Sequence from "../Interactivity/Sequence.vue";
    import Categorise from "../Interactivity/Categorise.vue";
    import Email from "../Interactivity/Email.vue";
    import EndSlide from "../UI/EndSlide.vue";
    import VideoSlide from "../UI/VideoSlide.vue";
    import {PubSub} from "@/Core/PubSub";
    import ResultsSlide from "../UI/ResultsSlide.vue";
    import AccessibilityGuide from "../UI/AccessibilityGuide.vue";
    import Settings from "../UI/Settings.vue";
    import ReaderModeSlide from "../UI/ReaderModeSlide.vue";
    import {settings, SettingsService} from "@/Core/Settings";
    import SettingsIcon from "../Icons/SettingsIcon.vue";
    import CaptionsIcon from "../Icons/CaptionsIcon.vue";
    import FullscreenIcon from "../Icons/FullscreenIcon.vue";
    import ExitIcon from "../Icons/ExitIcon.vue";
    import Website from "@/Interactivity/Website.vue";

    // We don't want users pulling down loads of extra js files for the creator tools.
    // Vue provides a feature called async components that can split some components out into other files.
    // To get it working with vue class components, this is using the method described here:
    // https://github.com/vuejs/vue-class-component/issues/323#issuecomment-624450239
    const AsyncCreatorTools = () => ({
      component: import('../CreatorTools/UI/SidebarPanel.vue')
    }) as any;
    const AsyncBottomPanel = () => ({
        component: import('../CreatorTools/UI/BottomPanel.vue')
    }) as any;
    const AsyncMenuBar = () => ({
        component: import('../CreatorTools/UI/MenuBar.vue')
    }) as any;
    const AsyncIconBar = () => ({
        component: import('../CreatorTools/UI/IconBar.vue')
    }) as any;
    const AsyncCreatorToolWindows = () => ({
        component: import('../CreatorTools/UI/CreatorToolWindows.vue')
    }) as any;
    const AsyncResizePanelBar = () => ({
        component: import('../CreatorTools/UI/ResizePanelBar.vue')
    }) as any;

    @Component({
        components: {
            AsyncCreatorTools,
            AsyncBottomPanel,
            AsyncMenuBar,
            AsyncIconBar,
            AsyncCreatorToolWindows,
            AsyncResizePanelBar,
            Website,
            ExitIcon,
            FullscreenIcon,
            CaptionsIcon,
            SettingsIcon,
            ReaderModeSlide,
            Settings,
            AccessibilityGuide,
            ResultsSlide,
            VideoSlide,
            EndSlide,
            Categorise,
            Sequence,
            ClosedCaptions,
            TitleSlide,
            ProgressIndicator,
            Slide,
            Navigation,
            MultipleChoice,
            Email
        }
    })
    export default class SlideshowView extends Vue {
        @VueInject("Synchroniser") sync: Synchroniser;
        @VueInject("Slideshow") slideshow: Slideshow;
        @VueInject("PubSub") pubSub: PubSub;
        @VueInject("SettingsService") settingsService: SettingsService;
        @VueInject("TutorialAttemptApi") api: TutorialAttemptApi;

        currentSlide: SlideData|null = null;

        @Prop({type: Boolean}) isCreatorModeEnabled: boolean;

        settings = settings;

        headerHeight = 58;
        footerHeight = 116;

        playbackSpeed = 1;

        showAccessibilityModal = false;
        showSettings = false;
        isReaderModeEnabled = true;

        created() {
            this.pubSub.subscribeToEvent(SlideshowEvents.Resize, () => this.resize());
            this.resize();
        }

        mounted() {
            this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntering, slideData => {
                this.currentSlide = slideData;
            });

            this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntered, () => {
                (<HTMLElement>this.$refs['main']).focus();
            });
            this.currentSlide = this.slideshow.currentSlide;
        }

        get customerLogoUrl() {
            if (this.slideshow.appearance.logoForBranding == null) {
                return null;
            }
            else {
                return this.slideshow.appearance.logoForBranding;
            }
        }

        get appearance() {
            return this.slideshow.appearance;
        }

        get supplierText() {
            if (this.slideshow.supplierText) {
                return this.slideshow.supplierText;
            }

            return "Cyber Awareness by";
        }

        get attempt() {
            return this.slideshow.attempt;
        }

        togglePlaybackSpeed() {
            let currentSpeed = this.sync.playbackSpeed;
            if (currentSpeed === 1) {
                this.sync.playbackSpeed = 0.75;
            }
            else if (currentSpeed === 0.75) {
                this.sync.playbackSpeed = 0.5;
            }
            else {
                this.sync.playbackSpeed = 1;
            }

            this.playbackSpeed = this.sync.playbackSpeed;
        }

        toggleClosedCaptions() {
            this.settingsService.setSetting('closedCaptions', !settings.closedCaptions);
        }

        toggleFullscreen() {
            toggleFullscreen();
        }

        get canExit() {
            return this.api.canExit();
        }

        closeTutorial() {
            this.slideshow.exit();
        }

        slideWidth = 0;
        slideHeight = 0;
        isSmallUi = false;

        startContentResize() {
            this.pubSub.publishEvent(SlideshowEvents.Resize, null);
        }

        @Watch('isCreatorModeEnabled')
        resize() {
            this.isSmallUi = window.innerHeight <= 600;
            // if creator mode is enable a 50px padding is applied all around the animations
            let uiHeight = this.isCreatorModeEnabled ? this.uiHeight + 100 : this.uiHeight;
            let uiWidth = this.isCreatorModeEnabled ? this.uiWidth + 100 : this.uiWidth;
            let availableWidth = window.innerWidth - uiWidth;
            let availableHeight = window.innerHeight - uiHeight;

            const defaultWidth = 1778;
            const defaultHeight = 826;

            let widthRatio = availableWidth / defaultWidth;
            let heightRatio = availableHeight / defaultHeight;

            let targetWidth: number;
            let targetHeight: number;

            if (widthRatio < heightRatio) {
                let ratio = availableWidth / defaultWidth;
                targetHeight = ratio * defaultHeight;
                targetWidth = availableWidth;
            }
            else {
                let ratio = availableHeight / defaultHeight;
                targetWidth = ratio * defaultWidth;
                targetHeight = availableHeight;
            }

            let fontSize = targetHeight / 82.75;
            document.documentElement.style.fontSize = fontSize + 'px';

            this.slideWidth = targetWidth;
            this.slideHeight = targetHeight;
        }

        // This is the total vertical height taken up non-slide UI elements
        timelineHeight = 300;
        sidebarWidth = 350;

        get uiHeight() {
            if (this.isCreatorModeEnabled) {
                // timeline + resize bar + icon bar + menu bar
                return this.timelineHeight + 4 + 24 + 24;
            }
            else if (this.isSmallUi) {
                return 25 + 35;
            }
            else {
                return 58 + 116;
            }
        }

        get uiWidth() {
            return this.isCreatorModeEnabled ? this.sidebarWidth : 0;
        }

        get creatorModeColumns() {
            return `${this.sidebarWidth}px 4px calc(100vw - ${this.sidebarWidth + 4}px`;
        }

        get creatorModeRows() {
            return `24px 24px 1fr 4px ${this.timelineHeight}px`;
        }
    }
